import { supabase } from "@/integrations/supabase/client";
import { format, parse, isWithinInterval, areIntervalsOverlapping } from "date-fns";
import { toZonedTime, fromZonedTime } from 'date-fns-tz';
import { BUSINESS_TIMEZONE, MINIMUM_BOOKING_LEAD_TIME_MINUTES } from './timezoneConfig';

import { getPrimaryAdminId } from './primaryAdmin';

export interface TimeSlot {
  startTime: Date;
  endTime: Date;
  isAvailable: boolean;
  isAfterHours: boolean;
  requiresApproval: boolean;
  reason?: string;
}

export interface AvailabilityResult {
  available: boolean;
  reason?: string;
  isAfterHours: boolean;
}

/**
 * Gets all available time slots for a specific date
 * Always uses the primary admin's availability for consistency
 * 
 * IMPORTANT: All slot times are generated in Central Time and stored as proper UTC Date objects.
 */
export async function getAvailableSlots(
  date: Date,
  adminId?: string
): Promise<TimeSlot[]> {
  try {
    // Convert selected date to CT to get the correct day of week
    const dateCT = toZonedTime(date, BUSINESS_TIMEZONE);
    const dayOfWeek = dateCT.getDay();
    const dateStr = format(dateCT, 'yyyy-MM-dd');
    
    // Always use primary admin ID for consistent scheduling
    const effectiveAdminId = adminId || await getPrimaryAdminId();

    // Fetch admin availability for this day of week
    const { data: availability, error: availError } = await supabase
      .from('admin_availability')
      .select('*')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true)
      .eq('admin_id', effectiveAdminId)
      .limit(1)
      .maybeSingle();

    if (availError) throw availError;
    if (!availability) return []; // No availability set for this day

    // Parse start and end times as CT hours/minutes
    const [startH, startM] = availability.start_time.split(':').map(Number);
    const [endH, endM] = availability.end_time.split(':').map(Number);
    const slotDuration = availability.slot_duration_minutes || 60;

    // Determine effective end time (includes after-hours if configured)
    let effectiveEndH = endH;
    let effectiveEndM = endM;
    if (availability.allow_after_hours && availability.after_hours_end) {
      const [ahEndH, ahEndM] = availability.after_hours_end.split(':').map(Number);
      const ahEndMinutes = ahEndH * 60 + ahEndM;
      const regEndMinutes = endH * 60 + endM;
      if (ahEndMinutes > regEndMinutes) {
        effectiveEndH = ahEndH;
        effectiveEndM = ahEndM;
      }
    }

    // Build slot start/end as proper UTC timestamps from CT components
    // Use fromZonedTime: "this date+time IS in CT, give me UTC"
    const [year, month, day] = dateStr.split('-').map(Number);
    
    const makeUTCFromCT = (h: number, m: number): Date => {
      const ctDate = new Date(year, month - 1, day, h, m, 0, 0);
      return fromZonedTime(ctDate, BUSINESS_TIMEZONE);
    };

    const slotStartUTC = makeUTCFromCT(startH, startM);
    const effectiveEndUTC = makeUTCFromCT(effectiveEndH, effectiveEndM);

    // Fetch blocked time slots for this date
    const { data: blockedSlots, error: blockedError } = await supabase
      .from('blocked_time_slots')
      .select('*')
      .eq('blocked_date', dateStr);

    if (blockedError) throw blockedError;

    // Fetch booked time slots for this date using the database function
    const { data: existingAppointments, error: aptError } = await supabase
      .rpc('get_booked_time_slots', { check_date: dateStr });

    if (aptError) throw aptError;

    // Generate time slots
    const slots: TimeSlot[] = [];
    let currentSlotStart = new Date(slotStartUTC);
    
    // Get current time as UTC for comparison
    const nowUTC = new Date();
    const minimumBookingUTC = new Date(nowUTC.getTime() + MINIMUM_BOOKING_LEAD_TIME_MINUTES * 60000);
    
    // Check if selected date is today in CT
    const nowCT = toZonedTime(nowUTC, BUSINESS_TIMEZONE);
    const isToday = format(dateCT, 'yyyy-MM-dd') === format(nowCT, 'yyyy-MM-dd');

    while (currentSlotStart < effectiveEndUTC) {
      const currentSlotEnd = new Date(currentSlotStart.getTime() + slotDuration * 60000);
      
      if (currentSlotEnd > effectiveEndUTC) break;

      // Skip slots that have already passed or don't have enough lead time (only for today)
      if (isToday && currentSlotStart <= minimumBookingUTC) {
        currentSlotStart = new Date(currentSlotEnd);
        continue;
      }

      // Check if slot is after hours - compare in CT minutes
      const slotStartCT = toZonedTime(currentSlotStart, BUSINESS_TIMEZONE);
      const slotStartCTMinutes = slotStartCT.getHours() * 60 + slotStartCT.getMinutes();
      
      let isAfterHours = false;
      if (availability.allow_after_hours && availability.after_hours_start && availability.after_hours_end) {
        const [ahStartH, ahStartM] = availability.after_hours_start.split(':').map(Number);
        const [ahEndH2, ahEndM2] = availability.after_hours_end.split(':').map(Number);
        const ahStartMinutes = ahStartH * 60 + ahStartM;
        const ahEndMinutes = ahEndH2 * 60 + ahEndM2;
        isAfterHours = slotStartCTMinutes >= ahStartMinutes && slotStartCTMinutes < ahEndMinutes;
      }

      // Check if slot is blocked - blocked times are stored as CT time strings
      const isBlocked = blockedSlots?.some(blocked => {
        if (blocked.is_all_day) return true;
        if (!blocked.start_time || !blocked.end_time) return false;
        
        const blockedStartUTC = makeUTCFromCT(
          ...blocked.start_time.split(':').map(Number).slice(0, 2) as [number, number]
        );
        const blockedEndUTC = makeUTCFromCT(
          ...blocked.end_time.split(':').map(Number).slice(0, 2) as [number, number]
        );
        
        return areIntervalsOverlapping(
          { start: currentSlotStart, end: currentSlotEnd },
          { start: blockedStartUTC, end: blockedEndUTC }
        );
      });

      // Check if slot conflicts with existing appointments (these are already UTC)
      const hasConflict = existingAppointments?.some(apt => {
        if (!apt.start_time || !apt.end_time) return false;
        
        const aptStart = new Date(apt.start_time);
        const aptEnd = new Date(apt.end_time);
        
        return areIntervalsOverlapping(
          { start: currentSlotStart, end: currentSlotEnd },
          { start: aptStart, end: aptEnd }
        );
      });

      let reason: string | undefined;
      let isAvailable = true;

      if (isBlocked) {
        isAvailable = false;
        reason = 'Time slot is blocked';
      } else if (hasConflict) {
        isAvailable = false;
        reason = 'Time slot is already booked';
      } else if (isAfterHours) {
        isAvailable = true;
        reason = 'After-hours appointment (requires approval)';
      }

      slots.push({
        startTime: new Date(currentSlotStart),
        endTime: new Date(currentSlotEnd),
        isAvailable,
        isAfterHours,
        requiresApproval: isAfterHours,
        reason,
      });

      currentSlotStart = new Date(currentSlotEnd);
    }

    return slots;
  } catch (error) {
    console.error('Error fetching available slots:', error);
    return [];
  }
}

/**
 * Checks if a specific time slot is available
 * Always uses the primary admin's availability for consistency
 */
export async function isTimeSlotAvailable(
  startTime: Date,
  endTime: Date,
  adminId?: string
): Promise<AvailabilityResult> {
  try {
    // Convert to CT for day-of-week and date checks
    const startCT = toZonedTime(startTime, BUSINESS_TIMEZONE);
    const dayOfWeek = startCT.getDay();
    const dateStr = format(startCT, 'yyyy-MM-dd');
    
    // Always use primary admin ID for consistent scheduling
    const effectiveAdminId = adminId || await getPrimaryAdminId();

    // Check admin availability
    const { data: availability, error: availError } = await supabase
      .from('admin_availability')
      .select('*')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true)
      .eq('admin_id', effectiveAdminId)
      .limit(1)
      .maybeSingle();

    if (availError) throw availError;
    if (!availability) {
      return { available: false, reason: 'Admin not available on this day', isAfterHours: false };
    }

    // Compare hours in CT
    const startCTMinutes = startCT.getHours() * 60 + startCT.getMinutes();
    const endCT = toZonedTime(endTime, BUSINESS_TIMEZONE);
    const endCTMinutes = endCT.getHours() * 60 + endCT.getMinutes();

    const [availStartH, availStartM] = availability.start_time.split(':').map(Number);
    const [availEndH, availEndM] = availability.end_time.split(':').map(Number);
    const availStartMinutes = availStartH * 60 + availStartM;
    const availEndMinutes = availEndH * 60 + availEndM;

    const isWithinRegularHours = startCTMinutes >= availStartMinutes && endCTMinutes <= availEndMinutes;

    // Check if after hours
    let isAfterHours = false;
    if (availability.allow_after_hours && availability.after_hours_start && availability.after_hours_end) {
      const [ahStartH, ahStartM] = availability.after_hours_start.split(':').map(Number);
      const [ahEndH, ahEndM] = availability.after_hours_end.split(':').map(Number);
      const ahStartMinutes = ahStartH * 60 + ahStartM;
      const ahEndMinutes = ahEndH * 60 + ahEndM;
      isAfterHours = startCTMinutes >= ahStartMinutes && endCTMinutes <= ahEndMinutes;
    }

    if (!isWithinRegularHours && !isAfterHours) {
      return { available: false, reason: 'Time outside available hours', isAfterHours: false };
    }

    // Check blocked time slots
    const { data: blockedSlots, error: blockedError } = await supabase
      .from('blocked_time_slots')
      .select('*')
      .eq('blocked_date', dateStr);

    if (blockedError) throw blockedError;

    const isBlocked = blockedSlots?.some(blocked => {
      if (blocked.is_all_day) return true;
      if (!blocked.start_time || !blocked.end_time) return false;
      
      const [bsH, bsM] = blocked.start_time.split(':').map(Number);
      const [beH, beM] = blocked.end_time.split(':').map(Number);
      const blockedStartMinutes = bsH * 60 + bsM;
      const blockedEndMinutes = beH * 60 + beM;
      
      return !(endCTMinutes <= blockedStartMinutes || startCTMinutes >= blockedEndMinutes);
    });

    if (isBlocked) {
      return { available: false, reason: 'Time slot is blocked', isAfterHours };
    }

    // Check existing appointments using the database function
    const { data: existingAppointments, error: aptError } = await supabase
      .rpc('get_booked_time_slots', { check_date: dateStr });

    if (aptError) throw aptError;

    const hasConflict = existingAppointments?.some(apt => {
      if (!apt.start_time || !apt.end_time) return false;
      
      const aptStart = new Date(apt.start_time);
      const aptEnd = new Date(apt.end_time);
      
      return areIntervalsOverlapping(
        { start: startTime, end: endTime },
        { start: aptStart, end: aptEnd }
      );
    });

    if (hasConflict) {
      return { available: false, reason: 'Time slot already booked', isAfterHours };
    }

    return { 
      available: true, 
      isAfterHours,
      reason: isAfterHours ? 'After-hours appointment (requires approval)' : undefined
    };
  } catch (error) {
    console.error('Error checking time slot availability:', error);
    return { available: false, reason: 'Error checking availability', isAfterHours: false };
  }
}

/**
 * Gets the configured slot duration for a specific date
 * Always uses the primary admin's settings
 */
export async function getSlotDuration(date: Date, adminId?: string): Promise<number> {
  try {
    const dateCT = toZonedTime(date, BUSINESS_TIMEZONE);
    const dayOfWeek = dateCT.getDay();
    
    const effectiveAdminId = adminId || await getPrimaryAdminId();

    const { data: availability, error } = await supabase
      .from('admin_availability')
      .select('slot_duration_minutes')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true)
      .eq('admin_id', effectiveAdminId)
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    return availability?.slot_duration_minutes || 60;
  } catch (error) {
    console.error('Error fetching slot duration:', error);
    return 60;
  }
}

/**
 * Checks if a time is in after-hours range
 * Always uses the primary admin's settings
 */
export async function isAfterHoursTime(time: Date, adminId?: string): Promise<boolean> {
  try {
    const timeCT = toZonedTime(time, BUSINESS_TIMEZONE);
    const dayOfWeek = timeCT.getDay();
    
    const effectiveAdminId = adminId || await getPrimaryAdminId();

    const { data: availability, error } = await supabase
      .from('admin_availability')
      .select('allow_after_hours, after_hours_start, after_hours_end')
      .eq('day_of_week', dayOfWeek)
      .eq('is_available', true)
      .eq('admin_id', effectiveAdminId)
      .limit(1)
      .maybeSingle();

    if (error) throw error;
    if (!availability?.allow_after_hours || !availability.after_hours_start || !availability.after_hours_end) {
      return false;
    }

    const timeCTMinutes = timeCT.getHours() * 60 + timeCT.getMinutes();
    const [ahStartH, ahStartM] = availability.after_hours_start.split(':').map(Number);
    const [ahEndH, ahEndM] = availability.after_hours_end.split(':').map(Number);
    const ahStartMinutes = ahStartH * 60 + ahStartM;
    const ahEndMinutes = ahEndH * 60 + ahEndM;
    
    return timeCTMinutes >= ahStartMinutes && timeCTMinutes < ahEndMinutes;
  } catch (error) {
    console.error('Error checking after hours:', error);
    return false;
  }
}
